import { useContext, useEffect, useRef, useState } from 'react';
import cloneDeep from 'lodash.clonedeep';
import set from 'lodash.set';
import unset from 'lodash.unset';
import axios from 'axios';
import get from 'lodash.get';

import type { DependenciesFormValues, FormValues } from '../../common/types/FormValues';
import { getFieldQueryByField } from '../../common/context/utils/getFieldQueryByField';
import type { TouchedValues } from '../../common/context/ContestManageContext';
import { ContestManageContext } from '../../common/context/ContestManageContext';
import { getDependenciesValues } from '../../common/context/utils/getDependencyValues';
import { getIsDependenciesSatisfied } from '../../common/context/utils/getIsDependenciesSatisfied';
import {
  createRemoveFieldCacheAction,
  createSetFieldCacheAction,
  createUpdateFieldAction,
  createUpdateFieldCacheAction,
} from '../../common/context/store/store.actions';
import type { FieldQuery, FieldQueryData } from '../../common/context/types/store';
import {
  isDebounceCancelError,
  useDebouncedCallbackWithPromise,
} from '../../common/context/utils/useDebouncedCallbackWithPromise';
import { useDebouncedEffect } from '~/domains/common/utils/useDebouncedEffect';
import { shouldPreserveFieldValue } from '../../common/context/utils/shouldPreserveFieldValue';

type QueryFn = (
  newQueryKey: string,
  payload: FormValues,
  abortController: AbortController,
  currentFieldQuery: FieldQueryData | null
) => Promise<any>;

export const useEnsureFieldData = ({
  field,
  fieldKey,
  sectionKey,
  queryFn,
  setTouched,
  setValues,
  path,
}: {
  field: FieldQuery;
  fieldKey: string;
  sectionKey: string;
  queryFn: QueryFn;
  path?: string[];
  setValues: (fn: (prev: FormValues) => FormValues) => void;
  setTouched: (fn: (prev: TouchedValues) => TouchedValues) => void;
}) => {
  const { store, dispatchStoreAction, values, errors } = useContext(ContestManageContext);
  const abortControllerRef = useRef<Record<string, AbortController>>({});
  const [retryFn, setRetryFn] = useState<() => void>(null);
  const storeRef = useRef(store);
  const prevDependencyValuesRef = useRef<DependenciesFormValues>({});

  const prevDependencyValues = prevDependencyValuesRef.current;
  storeRef.current = store;

  const debouncedQueryFn = useDebouncedCallbackWithPromise(queryFn, 500);

  // No idea why but this stops the race condition infinite loop
  useDebouncedEffect(
    () => {
      if (!field || (field.type !== 'DynamicField' && field.type !== 'DynamicFields')) {
        return;
      }

      const dependencyValues = getDependenciesValues(values, sectionKey, field.dependsOn);
      prevDependencyValuesRef.current = dependencyValues;

      const isDependenciesSatisfied = getIsDependenciesSatisfied(
        sectionKey,
        field.dependsOn,
        errors,
        dependencyValues
      );

      if (!isDependenciesSatisfied) {
        if (!field.latestQueryKey) {
          return;
        }
        dispatchStoreAction(createUpdateFieldAction(fieldKey, { latestQueryKey: undefined }));
        return;
      }

      const newFieldQueryKey = `${field.endpointName}:${JSON.stringify(dependencyValues)}`;
      const currentFieldQuery = getFieldQueryByField(storeRef.current, field);
      if (
        field.latestQueryKey === newFieldQueryKey &&
        (currentFieldQuery.isLoading || currentFieldQuery?.data || currentFieldQuery?.error)
      ) {
        return;
      }

      dispatchStoreAction(createUpdateFieldAction(fieldKey, { latestQueryKey: newFieldQueryKey }));

      if (!storeRef.current.fieldsCache[newFieldQueryKey]) {
        const prevQueryAbortController = abortControllerRef.current[field.latestQueryKey];
        if (prevQueryAbortController) {
          // FIXME: Temporary fix for aborting requests so all other reducer actions can be dispatched before cancellation
          setTimeout(() => {
            prevQueryAbortController.abort();
            abortControllerRef.current[field.latestQueryKey] = undefined;
          }, 1);
        }
        const newAbortController = new AbortController();
        abortControllerRef.current[newFieldQueryKey] = newAbortController;

        let retries = 0;
        const callQueryFn = () => {
          dispatchStoreAction(
            createSetFieldCacheAction(newFieldQueryKey, {
              key: field.key,
              type: field.type,
              isLoading: true,
            })
          );

          return debouncedQueryFn(
            newFieldQueryKey,
            dependencyValues,
            newAbortController,
            currentFieldQuery
          ).catch((error) => {
            if (axios.isCancel(error) || isDebounceCancelError(error)) {
              dispatchStoreAction(createRemoveFieldCacheAction(newFieldQueryKey));
              return;
            }

            if (axios.isAxiosError(error) && error.code === 'ERR_NETWORK') {
              if (retries < 2) {
                retries += 1;

                return new Promise((resolve) => {
                  setTimeout(() => {
                    resolve(callQueryFn());
                  }, 1000);
                });
              }
              retries = 0;
            }
            if (isDebounceCancelError(error)) {
              dispatchStoreAction(createRemoveFieldCacheAction(newFieldQueryKey));
              return;
            }

            dispatchStoreAction(
              createUpdateFieldCacheAction(newFieldQueryKey, {
                isLoading: false,
                error,
              })
            );
            setRetryFn(() => () => {
              callQueryFn();
            });
          });
        };

        callQueryFn();
      }
      if (currentFieldQuery.type === 'DynamicFields' || !currentFieldQuery.data) {
        return;
      }

      setValues((prev) => {
        const currentValue = get(prev, [sectionKey, ...path, currentFieldQuery.data.key]);

        if (typeof currentValue === 'undefined') {
          return prev;
        }

        const newPrev = cloneDeep(prev);

        const preserveCurrent = shouldPreserveFieldValue(
          prevDependencyValues,
          dependencyValues,
          sectionKey,
          field.dependsOn
        );

        if (preserveCurrent) {
          set(newPrev, [sectionKey, ...path, currentFieldQuery.data.key], currentValue);
        } else if (
          'defaultValue' in currentFieldQuery.data &&
          currentFieldQuery.data.defaultValue !== undefined &&
          currentFieldQuery.data.defaultValue !== null
        ) {
          set(
            newPrev,
            [sectionKey, ...path, currentFieldQuery.data.key],
            currentFieldQuery.data.defaultValue
          );
        } else {
          unset(newPrev, [sectionKey, ...path, currentFieldQuery.data.key]);
        }

        return newPrev;
      });
      setTouched((prev) => {
        if (typeof get(prev, [sectionKey, ...path, currentFieldQuery.data.key]) === 'undefined') {
          return prev;
        }

        const newPrev = cloneDeep(prev);
        unset(newPrev, [sectionKey, ...path, currentFieldQuery.data.key]);
        return newPrev;
      });
    },
    1,
    [
      field,
      fieldKey,
      sectionKey,
      debouncedQueryFn,
      JSON.stringify(values),
      JSON.stringify(errors),
      dispatchStoreAction,
      setValues,
      setTouched,
      JSON.stringify(path),
    ]
  );

  return {
    retryFn,
  };
};
